import React, { useEffect } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
// Removed StylesProvider and StylesThemeProvider - incompatible with React 18
// import { StylesProvider, ThemeProvider as StylesThemeProvider } from '@mui/styles';
// Temporarily disabled FlaggProvider due to React 18 compatibility issues
// import { FlaggProvider } from 'flagg/dist/react';
import { BrowserRouter } from 'react-router-dom';
import { SoundEffectProvider } from './components/SoundEffectProvider/SoundEffectProvider';
import { Toaster } from './components/Toaster/Toaster';
// import { featureFlags } from './featureFlags';
import { Routes } from './Routes';
import AppStateProvider from './state';
import { lavender as theme } from './theme/theme';
import client from './api/client';
import { AppLoader } from './components/AppLoader/AppLoader';

export const App: React.FC = () => {
  console.log('🚀 IneoSpace App - React 18 + Material-UI v5 + React Router v6');

  useEffect(() => {
    const fragment = window.location.hash.replace(/^#/, '');
    if (!fragment) return;

    let params: URLSearchParams;
    try {
      params = new URLSearchParams(fragment);
    } catch {
      return;
    }

    const joinGrant = params.get('joinGrant');
    const sessionToken = params.get('sessionToken');
    if ((!joinGrant || joinGrant.length < 20) && (!sessionToken || sessionToken.length < 8)) return;

    try {
      window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);
    } catch {
    }

    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const runHandoff = async (token: string) => {
      const maxRetries = 3;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const res = await client.post<{ actor: any; sessionToken: string; returnUrl?: string; shareUrl?: string }>(
            '/space/handoff',
            { joinGrant: token },
            client.SERVICES.api
          );

          const nextToken = res.sessionToken;
          if (typeof nextToken !== 'string' || nextToken.length < 8) return null;
          return { token: nextToken, returnUrl: res.returnUrl, shareUrl: res.shareUrl };
        } catch {
          if (attempt === maxRetries) return null;
          await sleep(500 * attempt);
        }
      }
      return null;
    };

    const bootstrap = async () => {
      if (typeof joinGrant === 'string' && joinGrant.length >= 20) {
        const nextToken = await runHandoff(joinGrant);
        if (!nextToken) return;
        localStorage.setItem('ndl_token', nextToken.token);
        localStorage.setItem('__session_token', nextToken.token);
        if (typeof nextToken.returnUrl === 'string') localStorage.setItem('space_return_url', nextToken.returnUrl);
        if (typeof nextToken.shareUrl === 'string') localStorage.setItem('space_share_url', nextToken.shareUrl);
        window.location.reload();
        return;
      }

      if (typeof sessionToken === 'string' && sessionToken.length >= 8) {
        localStorage.setItem('ndl_token', sessionToken);
        localStorage.setItem('__session_token', sessionToken);
        window.location.reload();
      }
    };

    void bootstrap();
  }, []);

  // Add global click listener to resume AudioContext
  useEffect(() => {
    const handleUserGesture = () => {
      client.socket.enableAudio();
    };
    window.addEventListener('click', handleUserGesture);
    window.addEventListener('keydown', handleUserGesture);
    return () => {
      window.removeEventListener('click', handleUserGesture);
      window.removeEventListener('keydown', handleUserGesture);
    };
  }, []);

  useEffect(() => {
    const rawAllowedOrigins = (process.env.REACT_APP_HANDOFF_ALLOWED_ORIGINS || '').trim();
    const allowedOrigins = rawAllowedOrigins
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);

    let referrerOrigin: string | null = null;
    try {
      if (document.referrer) {
        referrerOrigin = new URL(document.referrer).origin;
      }
    } catch {
      referrerOrigin = null;
    }

    const isAllowedOrigin = (origin: string) => {
      if (allowedOrigins.length > 0) {
        return allowedOrigins.includes(origin);
      }
      if (process.env.NODE_ENV === 'production') {
        return origin === window.location.origin;
      }
      try {
        const url = new URL(origin);
        return url.hostname === window.location.hostname || origin === referrerOrigin;
      } catch {
        return false;
      }
    };

    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const runHandoff = async (token: string) => {
      const maxRetries = 3;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const res = await client.post<{ actor: any; sessionToken: string; returnUrl?: string; shareUrl?: string }>(
            '/space/handoff',
            { joinGrant: token },
            client.SERVICES.api
          );

          const nextToken = res.sessionToken;
          if (typeof nextToken !== 'string' || nextToken.length < 8) return null;
          return { token: nextToken, returnUrl: res.returnUrl, shareUrl: res.shareUrl };
        } catch {
          if (attempt === maxRetries) return null;
          await sleep(500 * attempt);
        }
      }
      return null;
    };

    const handler = async (event: MessageEvent) => {
      if (!isAllowedOrigin(event.origin)) return;

      const data = event.data as any;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'NDL_AUTH_HANDOFF') return;

      const joinGrant = data.joinGrant;
      if (typeof joinGrant === 'string' && joinGrant.length >= 20) {
        const sessionToken = await runHandoff(joinGrant);
        if (!sessionToken) return;
        localStorage.setItem('ndl_token', sessionToken.token);
        localStorage.setItem('__session_token', sessionToken.token);
        if (typeof sessionToken.returnUrl === 'string') localStorage.setItem('space_return_url', sessionToken.returnUrl);
        if (typeof sessionToken.shareUrl === 'string') localStorage.setItem('space_share_url', sessionToken.shareUrl);

        try {
          (event.source as Window | null)?.postMessage({ type: 'NDL_AUTH_HANDOFF_ACK' }, event.origin);
        } catch {
        }

        window.location.reload();
        return;
      }

      const sessionToken = data.sessionToken;
      if (typeof sessionToken !== 'string' || sessionToken.length < 8) return;

      localStorage.setItem('ndl_token', sessionToken);
      localStorage.setItem('__session_token', sessionToken);

      try {
        (event.source as Window | null)?.postMessage({ type: 'NDL_AUTH_HANDOFF_ACK' }, event.origin);
      } catch {
      }

      window.location.reload();
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return (
    // Temporarily disabled FlaggProvider
    // <FlaggProvider featureFlags={featureFlags}>
    <ThemeProvider theme={theme}>
      <Toaster />
      <CssBaseline />
      {/* @ts-ignore: React Router v7 future flags supported in v6.30+ but types might be outdated */}
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppStateProvider>
          <SoundEffectProvider>
            <AppLoader>
              <Routes />
            </AppLoader>
          </SoundEffectProvider>
        </AppStateProvider>
      </BrowserRouter>
    </ThemeProvider>
    // </FlaggProvider>
  );
};
